import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { EmailAuthProvider, getMultiFactorResolver, reauthenticateWithCredential } from 'firebase/auth';
import type { MultiFactorError } from 'firebase/auth';
import type { PasskeyCredentialSummary } from '@usacsmideo/types';
import { auth } from '../constants/firebaseModular';
import { enrollPasskey, listMyPasskeys, revokeMyPasskey } from '../services/passkey/passkeyFlows';
import { describeThisBrowser } from '../utils/mfa/deviceLabel';
import { scrubSafeAuthCode } from '../utils/mfa/sentryAuthCode';
import type { Resolver } from './useMfaChallenge';

/**
 * Passkey ENROLLMENT for a signed-in patient — CU-86ajzr8x1 (sub-task 2 of
 * CU-86ajzr89h). Ported from the admin console's `usePasskeyEnrollment`
 * (CU-86ajxja33) at PR #468, plus `fea2c489` and `89d08a55`.
 *
 * ── THE RAIL — TWO LINKS, NOT THREE ───────────────────────────────────────
 *
 *   reauthenticateWithCredential  (explicit M-E step-up, password re-entered)
 *     → getIdToken(true)          (the reauthProof — server window is ≤120s)
 *       → enrollPasskey()         (challenge → browser ceremony → submit)
 *
 * ⚠️ TWO LINKS IS A REAL DIFFERENCE FROM SMS, NOT A SIMPLIFICATION. Compare
 * `useSmsEnrollment`, which needs a THIRD link (an authorization mint): SMS runs
 * `multiFactor().enroll()` client→Identity Platform DIRECTLY, with no server
 * hook, so the mint exists purely as evidence the reconciliation sweep can match
 * — and a missing one manufactures the exact signature of a planted factor.
 * Passkey register goes through OUR server, which verifies the attestation
 * against a server-issued challenge before storing anything. That is strictly
 * stronger evidence than a token we minted ourselves, so there is nothing to
 * mint and nothing to reconcile. ⚠️ DO NOT ADD A MINT HERE "FOR SYMMETRY" — it
 * would be a second, weaker record of an event the server already proved.
 *
 * ⚠️ WHAT *IS* THE SAME IS THE ORDERING DISCIPLINE. `reauthProofRef` is armed
 * ONLY after `getIdToken(true)` RESOLVES, and asserted immediately before the
 * ceremony. See the notes at each.
 *
 * ⚠️ SEAM DISCIPLINE. This hook talks to `passkeyFlows` and NOTHING ELSE under
 * `src/services/passkey/`. It must never import `passkeyClient` or
 * `webPasskeyProvider`: exactly one file in this repo imports
 * `@simplewebauthn/browser`, and that invariant is held by the layering rather
 * than by anyone remembering it.
 *
 * ── TWO PATHS TO THE SAME CEREMONY ────────────────────────────────────────
 *
 *   direct:  submitPassword → reauth ──────────────────────────────────┐
 *                                                                     ├→ runCeremony
 *   SMS:     submitPassword → auth/multi-factor-auth-required          │
 *              → resolver exposed → <MfaChallengeForm> → resolveSignIn │
 *              → completeSecondFactor ─────────────────────────────────┘
 *
 * The tail is shared (`runCeremony`) so the latch is armed and asserted in ONE
 * place.
 *
 * ⚠️ THE SMS PATH MUST NOT RE-DRIVE `reauthenticateWithCredential`. See the long
 * note on `completeSecondFactor`. This is the single most expensive lesson on
 * this workstream: the console shipped that re-drive to staging, it made
 * enrollment IMPOSSIBLE for every SMS-enrolled admin, and every test passed.
 *
 * ── PHI DISCIPLINE (M3) ───────────────────────────────────────────────────
 * ⚠️ NEVER send the password, the email, or a `credId` to Sentry. Codes,
 * messages and counts only. A `credId` is a credential identifier tied to one
 * account; the server logs a truncated prefix for correlation, which is where
 * that belongs.
 */

/** Where the ceremony is. `reset()` is the way back to 'idle'. */
export type PasskeyEnrollmentStep = 'idle' | 'ceremony' | 'done' | 'needs-second-factor';

/**
 * Why enrollment did not complete.
 *
 * ⚠️ EVERY MEMBER EXISTS BECAUSE THE REMEDY DIFFERS — the same criterion
 * `SmsEnrollmentError` applies, and the reason it has ten members.
 */
export type PasskeyEnrollmentError =
  | { kind: 'bad-password' }
  | { kind: 'stale-session' }
  | { kind: 'too-many-requests' }
  /** This browser cannot run WebAuthn at all. Offer nothing; say so plainly. */
  | { kind: 'unsupported' }
  /** The authenticator already holds a credential for this RP. Actionable. */
  | { kind: 'already-registered' }
  /**
   * The ceremony could not reach the server.
   *
   * ⚠️ DISTINCT FROM `generic` BECAUSE THE NEXT STEP DIFFERS — "reconnect and
   * this will work" versus "try again or use another method". Collapsing the two
   * is what left an offline patient at the mobile MFA gate reading "Setup could
   * not be completed" (CU-86ajwh66w).
   */
  | { kind: 'network' }
  | { kind: 'generic' };

/** Failure of a credential-management action (list / remove). */
export type PasskeyManagementError = { kind: 'not-found' } | { kind: 'network' } | { kind: 'generic' };

export interface UsePasskeyEnrollment {
  step: PasskeyEnrollmentStep;
  busy: boolean;
  error: PasskeyEnrollmentError | null;
  /**
   * The live resolver when `step === 'needs-second-factor'`, else null.
   *
   * ⚠️ A LIVE IdP OBJECT. It cannot survive serialization, so the consumer must
   * render `<MfaChallengeForm resolver={...} />` INLINE rather than navigating.
   */
  resolver: Resolver | null;
  /** Whether this browser can run WebAuthn ceremonies at all. */
  supported: boolean;

  /** The one step: reauth → proof → ceremony → submit. */
  submitPassword: (_password: string) => Promise<boolean>;
  /**
   * Continue after the inline SMS challenge resolves.
   *
   * Called from `<MfaChallengeForm onComplete>`. Mints the proof from the
   * session `resolveSignIn` just established and runs the same ceremony as the
   * direct path. ⚠️ It does NOT re-drive the reauth — see its implementation.
   */
  completeSecondFactor: () => Promise<boolean>;
  /** Abandon and return to the start. Clears the proof latch and the error. */
  reset: () => void;

  // ── Credential management ────────────────────────────────────────────────
  /** This surface's credentials, or null before the first successful load. */
  credentials: PasskeyCredentialSummary[] | null;
  credentialsLoading: boolean;
  managementError: PasskeyManagementError | null;
  refreshCredentials: () => Promise<void>;
  /** Remove ONE credential. ⚠️ Never revoke-all — see `revokeMyPasskey`. */
  removeCredential: (_credId: string) => Promise<boolean>;

  /**
   * ⚠️ TEST SEAM — NOT PRODUCTION API. DO NOT CALL FROM A COMPONENT.
   *
   * Clears the reauth-proof latch WITHOUT resetting the step, so a test can
   * reproduce the one state that isolates the rail: a hook that believes it is
   * mid-ceremony with no proof behind it — exactly what a reordering refactor
   * produces.
   *
   * This exists because `reset()` clears everything, so a rail test built on it
   * passes even when the latch assertion is deleted (some other guard refuses
   * instead), proving nothing. That false pass was found by mutation-testing the
   * SMS rail. Do not "simplify" this away.
   */
  __clearReauthProofForTest: () => void;
}

/**
 * Injection seam for tests. Production passes nothing and every call goes to the
 * real SDK / flows, so the ceremony under test is the real one.
 */
export interface PasskeyEnrollmentDeps {
  reauthenticate?: (_password: string) => Promise<void>;
  getReauthProof?: () => Promise<string>;
  enroll?: (_params: {
    userId: string;
    userName: string;
    userDisplayName: string;
    reauthProof: string;
    deviceLabel?: string;
  }) => Promise<Awaited<ReturnType<typeof enrollPasskey>>>;
  list?: () => Promise<Awaited<ReturnType<typeof listMyPasskeys>>>;
  revoke?: (_credId: string) => Promise<Awaited<ReturnType<typeof revokeMyPasskey>>>;
  isSupported?: () => boolean;
  /** Test seam for the best-effort device label. Production derives it. */
  getDeviceLabel?: () => string | undefined;
}

/** Whether this browser exposes the WebAuthn API at all. */
function browserSupportsWebAuthn(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';
}

export function usePasskeyEnrollment(deps: PasskeyEnrollmentDeps = {}): UsePasskeyEnrollment {
  const [step, setStep] = useState<PasskeyEnrollmentStep>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PasskeyEnrollmentError | null>(null);
  const [resolver, setResolver] = useState<Resolver | null>(null);

  const [credentials, setCredentials] = useState<PasskeyCredentialSummary[] | null>(null);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [managementError, setManagementError] = useState<PasskeyManagementError | null>(null);

  /**
   * ⚠️ THE RAIL. Set ONLY after `getIdToken(true)` has RESOLVED; asserted
   * immediately before the ceremony. Never set optimistically, never in a
   * `finally`, and never re-armed on a retry that did not re-authenticate.
   */
  const reauthProofRef = useRef<string | null>(null);

  /**
   * ⚠️ THERE IS DELIBERATELY NO RETAINED PASSWORD HERE.
   *
   * The console held one (`pendingPasswordRef`) across the SMS challenge because
   * its post-challenge re-drive needed a password credential a second time. That
   * re-drive was fatal and is gone, so nothing reads the password after the
   * first reauth and holding it would be storage with no consumer.
   *
   * The property to assert is therefore the STRONGER one — **never retained**,
   * not "retained, then cleared". Do not reintroduce retention: if some future
   * step needs a credential again, prefer re-prompting, and measure whether it
   * is needed at all before adding either.
   */

  /**
   * One-shot latch for `completeSecondFactor`.
   *
   * ⚠️ A REF, NOT `busy`, AND THE DIFFERENCE IS LOAD-BEARING. `busy` is STATE:
   * within a single tick it is stale, so two synchronous calls both read `false`
   * and both proceed.
   *
   * Released by `reset()` (cancel → retry must work) and on a failed ceremony, so
   * a genuine retry is possible. NOT released on success — once a credential
   * exists there is nothing to repeat.
   *
   * ⚠️ THIS IS THE SECOND HALF OF A TWO-PART DEFENCE. `MfaChallengeForm` now
   * guards its own completion effect (CU-86ajzr8x1 commit 1), but this latch
   * stays regardless: this hook is a public API and must not depend on every
   * caller invoking it exactly once. A shared component cannot assume every
   * caller's callback is idempotent, and a hook cannot assume its caller is
   * the fixed component.
   */
  const secondFactorRunRef = useRef(false);

  /**
   * The same one-shot discipline for the PRIMARY path (adversarial round 1, F5).
   *
   * ⚠️ `busy` STATE WAS THE ONLY GUARD HERE, AND THE REASONING THREE REFS UP
   * SAYS WHY THAT IS WRONG: state is stale within a tick, so two synchronous
   * calls both read `false` and both proceed. Measured — a double
   * `submitPassword` produced 2 reauth calls and 2 enroll calls, i.e. two
   * WebAuthn sheets and two credentials against the 5-credential cap.
   *
   * ⚠️ LIVE SEVERITY WAS LOW, AND IT IS STILL WORTH CLOSING. Both real UI paths
   * (button click and Enter-key submit) fire once, and the button carries a real
   * `disabled={busy || …}` — so this was an inconsistent rail rather than a live
   * double-enroll. But the PRIMARY path was the unguarded one while its sibling
   * carried a documented latch for the identical hazard, and a hook is a public
   * API that must not depend on its caller debouncing.
   *
   * Released in `submitPassword`'s `finally` on every exit — unlike
   * `secondFactorRunRef`, which is HELD on success. The difference is real: after
   * a successful ceremony this hook stays mounted and a patient may legitimately
   * enrol a second credential on another authenticator, whereas the
   * post-challenge continuation has a spent resolver and nothing to repeat.
   */
  const submitRunRef = useRef(false);

  /** Guards against setting state, or starting a ceremony, after unmount. */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const supported = deps.isSupported ? deps.isSupported() : browserSupportsWebAuthn();

  const reset = useCallback(() => {
    reauthProofRef.current = null;
    // Cancelling must leave a retry possible.
    secondFactorRunRef.current = false;
    setStep('idle');
    setError(null);
    setResolver(null);
  }, []);

  /**
   * Proof → latch → ceremony. THE SHARED TAIL OF BOTH PATHS.
   *
   * ⚠️ EXTRACTED SO THERE IS EXACTLY ONE CHECKPOINT. Two copies would be two
   * things to keep in step, and the SMS path — the one that is harder to
   * exercise — would be the copy that rots.
   *
   * Takes the user EXPLICITLY rather than reading `auth.currentUser` itself:
   * after `resolveSignIn` the SDK hands back a different user object, and the
   * caller is the only one that knows which is current.
   *
   * ⚠️ THE PROOF IS MINTED FROM THE PASSED USER, and that is the whole point of
   * the parameter. An earlier console version took the user explicitly and then
   * minted from `auth.currentUser` anyway — so on the SMS path the proof could
   * come from one user object while the credential registered against another.
   */
  const runCeremony = useCallback(
    async (user: {
      uid: string;
      email: string;
      getIdToken: (_forceRefresh?: boolean) => Promise<string>;
    }): Promise<boolean> => {
      // ⚠️ GATE THE SIDE EFFECT, NOT JUST THE STATE. Everything below launches
      // real work — a token refresh, then a WebAuthn sheet and a server-side
      // registration. An unmounted hook must not start any of it: a patient who
      // navigates away would otherwise get a browser passkey prompt on a page
      // they have left, and a credential would register with no UI to report it.
      if (!mountedRef.current) return false;

      // The reauth just advanced `auth_time`, so force a refresh to get a token
      // carrying it. THIS is the proof the server validates (≤120s).
      //
      // ⚠️ `user.getIdToken`, NOT `auth.currentUser!.getIdToken` — a transient
      // null (a token-refresh race, a concurrent signOut) would throw a
      // TypeError rather than a classified auth error.
      const reauthProof = deps.getReauthProof ? await deps.getReauthProof() : await user.getIdToken(true);

      /**
       * ⚠️ THE RAIL. Armed ONLY after the token refresh has RESOLVED, so the ref
       * never holds a value that no refresh produced.
       *
       * ⚠️ REPORTED HONESTLY: WITHIN THIS FUNCTION, THE POSITION OF THIS LINE IS
       * NOT CURRENTLY TEST-PINNED, AND THE CONSOLE'S COMMENT OVERSTATED IT.
       * Measured 2026-08-12 — three separate hoist mutants were applied and all
       * SURVIVED. The reason is structural: this arming, the step gate and the
       * `if (!proof)` assertion below run in ONE synchronous stretch with no
       * `await` between them, and the ceremony's params read the LOCAL
       * `reauthProof`. So a hoisted arming is either overwritten by this line, or
       * — if this line is deleted too — rejected by `tsc` under `noUnusedLocals`.
       *
       * Keep the ordering anyway, for two reasons that do not depend on a test:
       * it is free, and it stays correct if a future refactor introduces an
       * `await` between here and the assertion, which is exactly when the
       * ordering would become load-bearing. What IS pinned is that no stale or
       * forged proof can reach the ceremony — see the assertion below and the
       * `__clearReauthProofForTest` seam.
       */
      reauthProofRef.current = reauthProof;

      /**
       * ⚠️ THE STEP GATE IS NOT THE RAIL. This sits below the latch and swapping
       * the two is behaviourally UNOBSERVABLE — both are unreachable on a failed
       * refresh. The latch is what the ceremony rests on; this only moves the
       * panel. Stated explicitly because `useSmsEnrollment`'s equivalent comment
       * once claimed otherwise and had to be corrected.
       */
      if (mountedRef.current) setStep('ceremony');

      /**
       * ⚠️ ASSERTED AT THE POINT OF NO RETURN.
       *
       * On the DIRECT path this is unreachable — the latch is armed a few lines
       * up with no `await` between. On the SMS path it is not so obviously so:
       * that route crosses a challenge and a `resolveSignIn` before arriving
       * here, which is exactly the kind of journey that grows an early return or
       * an extracted helper later. One assertion covering both is the point of
       * the extraction.
       *
       * ⚠️ MUTATION-TESTING NOTE: a mutant deleting this branch survives on the
       * direct path for the reason above. That is defence-in-depth, not weak
       * coverage — do not delete the guard, and do not write a test that reaches
       * it by mutating hook internals, which would pin the mutant rather than
       * the property. The `__clearReauthProofForTest` seam is how a test reaches
       * this state honestly.
       */
      const proof = reauthProofRef.current;
      if (!proof) {
        Sentry.captureMessage('passkey enroll: ceremony blocked — no reauth proof', {
          level: 'error',
          tags: { feature: 'passkey', step: 'enroll-rail' },
        });
        if (mountedRef.current) {
          setError({ kind: 'generic' });
          setStep('idle');
        }
        return false;
      }

      /**
       * ⚠️ `deviceLabel` IS SUPPLIED HERE, AND BEFORE F2 IT WAS NOT (adversarial
       * round 1). The field was declared at three layers and rendered by the UI,
       * but no caller ever set it — so `passkeyFlows`' conditional spread never
       * fired, EVERY row showed `'Unnamed passkey'`, and every revoke
       * confirmation read "Remove this unnamed passkey, enrolled …". The list's
       * disambiguation story had degraded to date-only.
       *
       * ⚠️ A CONVENIENCE, NOT AN IDENTIFIER. It is best-effort by contract:
       * never a revoke key, never the sort key, and never presented as telling
       * two credentials apart — `createdAt` does that. Omitted entirely when it
       * cannot be determined, rather than sent as a placeholder.
       *
       * ⚠️ Both sibling repos have the same gap (the console, and the mobile app,
       * which threads the parameter but has no caller that supplies one). Fixing
       * it here makes the portal diverge, deliberately — see the sub-task plan.
       */
      const deviceLabel = deps.getDeviceLabel ? deps.getDeviceLabel() : describeThisBrowser();

      const params = {
        userId: user.uid,
        userName: user.email,
        userDisplayName: user.email,
        reauthProof: proof,
        ...(deviceLabel ? { deviceLabel } : {}),
      };
      const outcome = deps.enroll ? await deps.enroll(params) : await enrollPasskey(params);

      if (!mountedRef.current) return outcome.status === 'enrolled';

      switch (outcome.status) {
        case 'enrolled':
          setStep('done');
          return true;
        /**
         * ⚠️ `cancelled` IS NOT AN ERROR AND TAKES NO CAPTURE. The patient
         * dismissed the browser sheet — an ordinary choice, not a fault.
         * Rendering an error is how a deliberate cancel becomes a support call.
         */
        case 'cancelled':
          setStep('idle');
          return false;
        case 'unsupported':
          setStep('idle');
          setError({ kind: 'unsupported' });
          return false;
        case 'already-registered':
          setStep('idle');
          setError({ kind: 'already-registered' });
          return false;
        case 'network':
          setStep('idle');
          setError({ kind: 'network' });
          return false;
        default:
          setStep('idle');
          setError({ kind: 'generic' });
          return false;
      }
    },
    [deps],
  );

  /** Step 1 — the M-E step-up, then the shared ceremony tail. */
  const submitPassword = useCallback(
    async (password: string): Promise<boolean> => {
      // ⚠️ THE REF IS CHECKED FIRST, AND IT IS THE ONE THAT HOLDS. `busy` is
      // retained alongside it because it is what the button's `disabled` reads,
      // but it cannot serialise two calls inside one tick — see `submitRunRef`.
      if (submitRunRef.current || busy) return false;
      submitRunRef.current = true;
      setBusy(true);
      setError(null);
      try {
        const user = auth.currentUser;
        if (!user?.email) {
          // Only reachable from an authenticated screen, so this is defensive
          // rather than an expected path.
          setError({ kind: 'generic' });
          return false;
        }

        // ── M-E step-up: EXPLICIT reauthentication, right now ──────────────
        //
        // ⚠️ NON-NEGOTIABLE, AND NOT SOFTENABLE TO "they logged in recently".
        // Firebase's time-based `requires-recent-login` would let a session
        // hijacked minutes after a legitimate login sail through — and a planted
        // credential grants the attacker durable FUTURE access from their own
        // device. Because we accept cloud-synced passkeys, device-binding is not
        // what stops a plant; this reauth is.
        try {
          if (deps.reauthenticate) {
            await deps.reauthenticate(password);
          } else {
            // Web uses the STANDALONE modular form. (Mobile deliberately uses
            // the instance method because the standalone misbehaved on RNFB
            // v22 — that is an RNFB bug, not a web one.)
            await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
          }
        } catch (reauthError) {
          const code = (reauthError as { code?: string })?.code ?? '';

          // ── THE SMS-ENROLLED PATIENT BRANCH ─────────────────────────────
          //
          // ⚠️ NOT AN ERROR — THE PASSWORD WAS CORRECT. A patient who already
          // has SMS enrolled CANNOT complete `reauthenticateWithCredential` with
          // a password alone: Identity Platform throws
          // `auth/multi-factor-auth-required` even on a correct password. That
          // population is exactly who wants passkeys, so it must not be a dead
          // end — and rendering "that password didn't match" over a perfectly
          // good password is the bug class that locked an enrolled admin out of
          // staging on 2026-07-30.
          if (code === 'auth/multi-factor-auth-required') {
            const mfaResolver = getMultiFactorResolver(auth, reauthError as MultiFactorError) as unknown as
              | Resolver
              | null;
            if (!mfaResolver) {
              // The SDK gave us the error but no resolver — nothing to render a
              // challenge from. A real fault, not an ordinary outcome.
              Sentry.captureException(reauthError, {
                tags: { feature: 'passkey', step: 'enroll-mfa-resolver' },
              });
              setError({ kind: 'generic' });
              return false;
            }
            if (mountedRef.current) {
              setResolver(mfaResolver);
              setStep('needs-second-factor');
            }
            Sentry.addBreadcrumb({
              category: 'passkey',
              level: 'info',
              message: 'passkey enroll: second factor required',
            });
            // ⚠️ THE PASSWORD IS NOT RETAINED PAST THIS POINT. It goes out of
            // scope with this closure — see the note where the ref used to be.
            return false;
          }

          const isBadPassword = code === 'auth/wrong-password' || code === 'auth/invalid-credential';
          const isStaleSession = code === 'auth/requires-recent-login';
          const isThrottled = code === 'auth/too-many-requests';
          // Ordinary user-facing outcomes get a breadcrumb; anything else is a
          // real fault we must SEE. Same policy as the SMS rail.
          if (!isBadPassword && !isStaleSession && !isThrottled) {
            Sentry.captureException(reauthError, {
              tags: { feature: 'passkey', step: 'enroll-reauth', auth_error_code: scrubSafeAuthCode(code) },
            });
          } else {
            Sentry.addBreadcrumb({
              category: 'passkey',
              level: 'warning',
              message: 'passkey enroll step-up: password rejected',
              data: { code: scrubSafeAuthCode(code) },
            });
          }
          setError(
            isBadPassword
              ? { kind: 'bad-password' }
              : isStaleSession
                ? { kind: 'stale-session' }
                : isThrottled
                  ? { kind: 'too-many-requests' }
                  : { kind: 'generic' },
          );
          return false;
        }

        return await runCeremony({ uid: user.uid, email: user.email, getIdToken: (f) => user.getIdToken(f) });
      } catch (err) {
        const code = (err as { code?: string })?.code ?? '';
        const message = (err as { message?: string })?.message ?? '';
        // The server rejects a proof older than 120s with
        // failed-precondition/"reauth-required". Same remedy as the client-side
        // stale case: ask for the password again.
        const isStale =
          code === 'auth/requires-recent-login' ||
          code === 'functions/failed-precondition' ||
          message.includes('reauth-required');
        const isThrottled = code === 'functions/resource-exhausted' || code === 'auth/too-many-requests';

        if (isStale || isThrottled) {
          Sentry.addBreadcrumb({
            category: 'passkey',
            level: 'warning',
            message: 'passkey enroll: proof refused',
            data: { code: scrubSafeAuthCode(code) },
          });
        } else {
          Sentry.captureException(err, {
            tags: { feature: 'passkey', step: 'enroll-proof', auth_error_code: scrubSafeAuthCode(code) },
            extra: { message: message || '(none)' },
          });
        }
        if (mountedRef.current) {
          setError(isStale ? { kind: 'stale-session' } : isThrottled ? { kind: 'too-many-requests' } : { kind: 'generic' });
          setStep('idle');
        }
        return false;
      } finally {
        // ⚠️ RELEASED ON EVERY EXIT, INCLUDING SUCCESS — deliberately unlike
        // `secondFactorRunRef`, which is held. A patient who has just enrolled
        // may legitimately add a second passkey on another authenticator from
        // this same mounted panel, so wedging the primary path after success
        // would break a supported flow. Its job is to serialise concurrent
        // calls, not to make enrollment once-per-mount.
        //
        // ⚠️ NOT gated on `mountedRef`: the ref belongs to this hook instance and
        // must be released even if the panel unmounted mid-flight, or a remount
        // reusing the instance would find it stuck.
        submitRunRef.current = false;
        if (mountedRef.current) setBusy(false);
      }
    },
    [busy, deps, runCeremony],
  );

  /**
   * Continue enrollment after the inline SMS challenge resolves.
   *
   * The UI calls this from `<MfaChallengeForm onComplete>`, which fires only once
   * `useMfaChallenge` has awaited `resolveSignIn` — so by the time we run, the
   * second factor is already satisfied.
   *
   * ── ⚠️⚠️ DO NOT RE-DRIVE THE REAUTH HERE. IT IS FATAL, NOT REDUNDANT ──────
   *
   * The console shipped a version that re-ran `reauthenticateWithCredential` at
   * this point, hedging an unmeasured question. It made this path IMPOSSIBLE TO
   * COMPLETE for every SMS-enrolled admin — two of three — and **every test
   * passed throughout**. Removed 2026-08-12 (`e1864c05`, PR #468) after a
   * staging measurement.
   *
   * WHAT WAS MEASURED (deployed staging, real SMS-enrolled admin):
   *
   *     TOKEN_AFTER_RESOLVE_CACHED     auth_time age 0s, iat age 0s
   *     TOKEN_AFTER_RESOLVE_REFRESHED  auth_time age 0s, iat age 0s
   *     REDRIVE_THREW                  auth/multi-factor-auth-required
   *     (re-drive skipped) → CEREMONY_OUTCOME  status: "enrolled"
   *
   * Two facts:
   *
   *  1. `resolveSignIn` DOES advance `auth_time` — the token is freshly minted
   *     and comfortably inside the server's 120s window. (`iat` was read
   *     alongside deliberately: reading `auth_time` alone previously produced a
   *     FALSE REJECT, because an identical `iat` means no new token was minted
   *     and the reading was a stale cache rather than evidence.)
   *  2. The re-drive CANNOT SUCCEED. `reauthenticateWithCredential` is a FRESH
   *     reauthentication, so Identity Platform re-applies the MFA policy to it
   *     and throws `auth/multi-factor-auth-required` again — the very state the
   *     challenge just exited.
   *
   * ⚠️ THIS IS NOT SKIPPING M-E. The invariant is **"an explicit fresh step-up
   * happened just now"**, NOT **"`reauthenticateWithCredential` was the function
   * that ran"**. A completed SMS challenge is a STRONGER proof than a re-typed
   * password, and the server still validates the resulting token's freshness.
   * **Do not "restore" the reauth on this path — that is the loop.**
   *
   * That framing is `mideo-health-app`'s, from `e7e2aa8` (CU-86ajw1kw5,
   * 2026-08-04) — which solved this EIGHT DAYS BEFORE the console hit it. On
   * device, the first attempt at the fix looped visibly: each re-thrown error
   * remounted the challenge, whose `sent` ref reset and auto-dispatched another
   * code, clearing the entry box, forever. It is written out here so a future
   * reviewer cannot "restore" the reauth as an apparent security improvement.
   * Both sibling repos carry this reasoning; the portal makes it three.
   *
   * ⚠️ NO `stepUpAlreadySatisfied` FLAG. Mobile carries one because it branches
   * on the `auth_time` question. That question is answered, so there is nothing
   * to branch on — a flag would encode a settled fact as if it were still in
   * doubt. (Divergence from mobile, named deliberately.)
   */
  const completeSecondFactor = useCallback(async (): Promise<boolean> => {
    // ⚠️ THE LATCH, CHECKED BEFORE ANYTHING ELSE. See `secondFactorRunRef`.
    if (secondFactorRunRef.current || busy) return false;
    secondFactorRunRef.current = true;

    setBusy(true);
    setError(null);
    try {
      // ⚠️ RE-READ `currentUser` AFTER `resolveSignIn`. The SDK hands back a
      // fresh user object once the challenge resolves, and the proof must be
      // minted from THAT one — reusing a pre-challenge reference is how a proof
      // silently targets a stale session. Both sibling repos do this.
      const user = auth.currentUser;
      if (!user?.email) {
        // No session to mint a proof from. Fail closed rather than half-run.
        // Released so a retry is possible once a session exists again.
        secondFactorRunRef.current = false;
        setError({ kind: 'generic' });
        return false;
      }

      const ok = await runCeremony({ uid: user.uid, email: user.email, getIdToken: (f) => user.getIdToken(f) });

      // ⚠️ RELEASED ONLY ON FAILURE. A failed ceremony (cancelled sheet,
      // network, server refusal) must leave a RETRY possible — wedging the panel
      // until the patient finds "Cancel" would be worse than the double-enroll
      // this latch prevents. On SUCCESS it stays held: a credential exists and
      // there is nothing to repeat.
      if (!ok) secondFactorRunRef.current = false;
      return ok;
    } catch (err) {
      secondFactorRunRef.current = false;
      Sentry.captureException(err, {
        tags: { feature: 'passkey', step: 'enroll-second-factor' },
      });
      if (mountedRef.current) {
        setError({ kind: 'generic' });
        setStep('idle');
      }
      return false;
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        // The resolver is spent on every exit path — a later failure must not be
        // able to re-render a challenge that has already been consumed.
        setResolver(null);
      }
    }
  }, [busy, runCeremony]);

  /**
   * Load this surface's credentials.
   *
   * ⚠️ NOT RE-SORTED. The server orders newest-first by `createdAt`; re-sorting
   * here risks keying on `deviceLabel`, which is client-supplied and can be
   * identical across devices.
   */
  const refreshCredentials = useCallback(async (): Promise<void> => {
    setCredentialsLoading(true);
    setManagementError(null);
    try {
      const outcome = deps.list ? await deps.list() : await listMyPasskeys();
      if (!mountedRef.current) return;
      if (outcome.status === 'listed') {
        setCredentials(outcome.credentials);
      } else {
        setManagementError(outcome.status === 'network' ? { kind: 'network' } : { kind: 'generic' });
      }
    } finally {
      if (mountedRef.current) setCredentialsLoading(false);
    }
  }, [deps]);

  /**
   * Remove ONE credential — the one named by `credId`, never another and never
   * all of them.
   *
   * ⚠️ TARGETED, ALWAYS. `revokeMyPasskey` takes a `credId`; substituting a
   * revoke-all would also clear this patient's mobile credentials and sign them
   * out everywhere. The driving case is "revoke the stolen phone, NOT the laptop
   * in my hand", and a mis-target inverts exactly that: the patient loses the
   * credential in their hand while the UI reports success. Invisible from the
   * screen either way — the control still succeeds.
   *
   * ⚠️ WHAT IS ACTUALLY PINNED, STATED PRECISELY (adversarial round 1, F1).
   * This comment previously claimed "a named test pins it rather than trusting
   * review". That was FALSE when written: the test's fixture returned an EMPTY
   * credential list, so a mutant revoking `credentials?.[0]?.credId ?? credId`
   * had nothing to mis-target with, fell through to the correct id, and passed
   * all 556 tests while typechecking clean.
   *
   * It is pinned now, and measured:
   *  • The hook test holds a POPULATED list whose first entry is NOT the target,
   *    so "revoke the first", "revoke every", and "revoke the one you were
   *    handed" are distinguishable. Both a mis-target and a loop now fail.
   *  • The section test independently kills a mis-target at its own layer by
   *    clicking the SECOND row of two.
   *
   * ⚠️ Do NOT add `credentials` to this callback's dependency array. Nothing in
   * here should read the list — the `credId` argument is the only input that may
   * determine the target. A version that consults the list is the mutant.
   * (This repo deliberately wraps no revoke-all at all; see `passkeyClient.ts`.)
   */
  const removeCredential = useCallback(
    async (credId: string): Promise<boolean> => {
      setManagementError(null);
      const outcome = deps.revoke ? await deps.revoke(credId) : await revokeMyPasskey(credId);
      if (!mountedRef.current) return outcome.status === 'revoked';
      if (outcome.status === 'revoked') {
        await refreshCredentials();
        return true;
      }
      setManagementError(
        outcome.status === 'not-found'
          ? { kind: 'not-found' }
          : outcome.status === 'network'
            ? { kind: 'network' }
            : { kind: 'generic' },
      );
      return false;
    },
    [deps, refreshCredentials],
  );

  return {
    step,
    busy,
    error,
    resolver,
    supported,
    submitPassword,
    completeSecondFactor,
    reset,
    credentials,
    credentialsLoading,
    managementError,
    refreshCredentials,
    removeCredential,
    __clearReauthProofForTest: () => {
      reauthProofRef.current = null;
    },
  };
}
